import React from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip, Link, makeStyles, createStyles } from '@bb-ui/react-library';
import { Warning, Check as CheckSmall, Close as CloseSmall } from '@bb-ui/icons/dist/small';
import { Check as CheckMedium, Close as CloseMedium } from '@bb-ui/icons/dist/medium';
import { LearnDataExport } from 'hooks/useLearnDataExport';
import { isLearnOrUnsetType } from 'utils/tenantType';

const useStyles = makeStyles((theme) =>
  createStyles({
    regionWarning: {
      display: 'inline-flex',
      color: theme.palette.warning.main,
      flexShrink: 0,
    },
    // Round badge with a light version of the icon color as background
    statusIcon: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      verticalAlign: 'middle',
      borderRadius: '50%',
      backgroundColor: 'color-mix(in srgb, currentColor 15%, transparent)',
    },
    statusIconSmall: {
      padding: theme.spacing(0.5),
    },
    statusIconMedium: {
      padding: theme.spacing(0.75),
    },
    statusTrue: {
      color: theme.palette.success.main,
    },
    statusFalse: {
      color: theme.palette.error.main,
    },
    statusLink: {
      display: 'inline-flex',
      borderRadius: '50%',
    },
    statusText: {
      color: theme.palette.text.secondary,
      fontStyle: 'italic',
    },
  }),
);

interface StatusBadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  value: boolean;
  size?: 'small' | 'medium';
}

/** The round badge only, without tooltip. Forwards the ref so it can be a tooltip target. */
const StatusBadge = React.forwardRef<HTMLSpanElement, StatusBadgeProps>(
  ({ value, size = 'small', className, ...spanProps }, ref) => {
    const classes = useStyles();
    const icons = size === 'medium' ? [CheckMedium, CloseMedium] : [CheckSmall, CloseSmall];
    const Icon = value ? icons[0] : icons[1];
    const sizeClass = size === 'medium' ? classes.statusIconMedium : classes.statusIconSmall;
    const colorClass = value ? classes.statusTrue : classes.statusFalse;

    return (
      <span
        ref={ref}
        className={`${classes.statusIcon} ${sizeClass} ${colorClass} ${className ?? ''}`}
        {...spanProps}
      >
        <Icon />
      </span>
    );
  },
);

export interface BooleanStatusIconProps {
  value: boolean;
  /** Text for the tooltip and screen readers */
  label: string;
  size?: 'small' | 'medium';
  /**
   * Lets keyboard users focus the badge to see the tooltip.
   * Use it when the tooltip explains more than the yes or no that screen readers already get.
   */
  focusable?: boolean;
}

/** Green check for true, red cross for false */
export const BooleanStatusIcon: React.FC<BooleanStatusIconProps> = ({
  value,
  label,
  size,
  focusable = false,
}) => (
  <Tooltip title={label}>
    <StatusBadge
      value={value}
      size={size}
      role="img"
      aria-label={label}
      tabIndex={focusable ? 0 : undefined}
    />
  </Tooltip>
);

interface StatusLinkProps {
  value: boolean;
  href: string;
  label: string;
}

/** Badge that opens a page in a new tab. The tooltip is on the link, so there is one tab stop. */
const StatusLink: React.FC<StatusLinkProps> = ({ value, href, label }) => {
  const classes = useStyles();

  return (
    <Tooltip title={label}>
      <Link
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={classes.statusLink}
        aria-label={label}
      >
        <StatusBadge value={value} aria-hidden />
      </Link>
    </Tooltip>
  );
};

export interface DataExportStatusProps {
  tenantType: string | null;
  /** Only used for Learn tenants. Missing means the lookup is still running. */
  learnDataExport?: LearnDataExport;
  /**
   * When set, the cross of a Learn tenant without data export links to the jobs of this tenant,
   * where the DMS can be provisioned.
   */
  jobsLinkTenantId?: string;
}

const getStatusType = (tenantType: string | null) =>
  isLearnOrUnsetType(tenantType) ? 'learn' : tenantType?.toLowerCase();

export const getDataExportLabelKey = ({
  tenantType,
  learnDataExport,
  jobsLinkTenantId,
}: DataExportStatusProps) => {
  switch (getStatusType(tenantType)) {
    case 'ally':
      return 'dataSharing.dataExportAlly';
    case 'learn':
      if (learnDataExport === undefined) return 'dataSharing.dataExportLoading';
      if (learnDataExport === null) return 'dataSharing.dataExportUnknown';
      if (learnDataExport) return 'dataSharing.dataExportOpenDms';
      return jobsLinkTenantId
        ? 'dataSharing.dataExportDisabledOnTenant'
        : 'dataSharing.dataExportDisabled';
    default:
      return 'dataSharing.dataExportUnsupported';
  }
};

export const DataExportStatus: React.FC<DataExportStatusProps> = (props) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const label = t(getDataExportLabelKey(props), { tenantId: props.learnDataExport });

  switch (getStatusType(props.tenantType)) {
    case 'ally':
      return <BooleanStatusIcon value label={label} focusable />;
    case 'learn':
      if (typeof props.learnDataExport === 'string') {
        return <StatusLink value href={`/tenants/${props.learnDataExport}`} label={label} />;
      }
      if (props.learnDataExport === false && props.jobsLinkTenantId) {
        return (
          <StatusLink
            value={false}
            href={`/tenants/${props.jobsLinkTenantId}/jobs`}
            label={label}
          />
        );
      }
      if (props.learnDataExport === false) {
        return <BooleanStatusIcon value={false} label={label} focusable />;
      }
      // Still loading or the lookup failed
      return <span className={classes.statusText}>{label}</span>;
    default:
      return <BooleanStatusIcon value={false} label={label} focusable />;
  }
};

export interface RegionMismatchWarningProps {
  tenantId: string;
  /** Nothing is shown while this is not known */
  tenantRegion?: string;
  /** Tenant type the tenant region was taken from, used in the warning text */
  regionSource?: string;
  requestingRegion?: string;
}

/** True only when both regions are known and they are different */
export const isRegionMismatch = (tenantRegion?: string, requestingRegion?: string) =>
  !!tenantRegion && !!requestingRegion && tenantRegion !== requestingRegion;

/** Warning icon shown when a tenant is not in the same region as the Illuminate tenant */
export const RegionMismatchWarning: React.FC<RegionMismatchWarningProps> = ({
  tenantId,
  tenantRegion,
  regionSource = 'Tenant',
  requestingRegion,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  if (!isRegionMismatch(tenantRegion, requestingRegion)) return null;

  const text = t('dataSharing.regionMismatch', {
    source: regionSource,
    tenantRegion,
    requestingRegion,
  });

  return (
    <Tooltip title={text}>
      <span
        className={classes.regionWarning}
        role="img"
        aria-label={text}
        tabIndex={0}
        data-testid={`region-warning-${tenantId}`}
      >
        <Warning />
      </span>
    </Tooltip>
  );
};
